import Phaser from 'phaser';
import { eventBus } from '@/shared/EventBus';

const FACE = 'boss_halloween_candy_glutton';
const DURATION = 8400;
const clamp = (n: number) => Phaser.Math.Clamp(n, 0, 1);
const smooth = (n: number) => { const p = clamp(n); return p * p * (3 - 2 * p); };

/** Cosmetic overlay only: it neither creates a fight nor awards/spawns gameplay candy. */
export class HalloweenBossIntroScene extends Phaser.Scene {
  private complete?: () => void;
  private finished = false;
  private elapsed = 0;
  private stage!: Phaser.GameObjects.Container;
  private background!: Phaser.GameObjects.TileSprite;
  private shade!: Phaser.GameObjects.Rectangle;
  private boss!: Phaser.GameObjects.Image;
  private shadow!: Phaser.GameObjects.Ellipse;
  private belly!: Phaser.GameObjects.Container;
  private bellyCandies: Phaser.GameObjects.Image[] = [];
  private candies: { sprite: Phaser.GameObjects.Image; x: number; y: number; start: number }[] = [];
  private sparkles: Phaser.GameObjects.Star[] = [];
  private caption!: Phaser.GameObjects.Text;
  private comic!: Phaser.GameObjects.Text;

  constructor() { super({ key: 'HalloweenBossIntroScene' }); }
  init(data: { onComplete: () => void }): void {
    this.complete = data.onComplete; this.finished = false; this.elapsed = 0;
    this.candies = []; this.sparkles = []; this.bellyCandies = [];
  }
  create(): void {
    this.background = this.add.tileSprite(0, 0, 1, 1, 'bg_capibara_pumpkin_patch_calm_tile').setOrigin(0);
    this.shade = this.add.rectangle(0, 0, 1, 1, 0x51335d, 0.28).setOrigin(0);
    this.stage = this.add.container(0, 0);
    const add = <T extends Phaser.GameObjects.GameObject>(object: T): T => { this.stage.add(object); return object; };
    add(this.add.ellipse(450, 620, 820, 360, 0xffe6ac, 0.92).setStrokeStyle(8, 0xfff4d4, 0.7));
    for (const x of [65, 835]) add(this.add.image(x, 355, 'deco_halloween_autumn_grove').setDisplaySize(245, 300).setAlpha(0.65));
    add(this.add.image(450, 155, 'deco_halloween_festival_lights').setDisplaySize(560, 300).setAlpha(0.8));
    this.shadow = add(this.add.ellipse(450, 616, 360, 70, 0x6f4868, 0.2));
    this.boss = add(this.add.image(450, 410, FACE).setDisplaySize(440, 440));
    for (let i = 0; i < 15; i++) {
      const group = Math.floor(i / 5), index = i % 5;
      const x = [185, 710, 455][group]! + (index % 3 - 1) * 42;
      const y = [650, 660, 717][group]! - Math.floor(index / 3) * 29;
      this.candies.push({ sprite: add(this.add.image(x, y, 'ui_candy').setDisplaySize(53, 53).setAngle((i % 3 - 1) * 22)),
        x, y, start: 1450 + group * 1050 + index * 65 });
    }
    // A magical candy window over the lower belly, with no anatomy or actual drops.
    this.belly = add(this.add.container(450, 549).setAlpha(0));
    this.belly.add(this.add.ellipse(0, 0, 218, 114, 0xffe786, 0.32));
    this.belly.add(this.add.ellipse(0, 0, 192, 96, 0xfff4c4, 0.9).setStrokeStyle(4, 0xffffff, 0.9));
    for (let i = 0; i < 7; i++) {
      const angle = i * Math.PI * 2 / 7;
      const candy = this.add.image(Math.cos(angle) * 57, Math.sin(angle) * 24, 'ui_candy').setDisplaySize(41, 41);
      this.belly.add(candy); this.bellyCandies.push(candy);
    }
    for (let i = 0; i < 12; i++) this.sparkles.push(add(this.add.star(450, 450, 4, 3, 10, 0xfff6c3).setAlpha(0)));
    this.comic = add(this.add.text(675, 350, '', { fontFamily: 'Nunito, sans-serif', fontSize: '53px', fontStyle: 'bold',
      color: '#78478e', stroke: '#fff5d6', strokeThickness: 10 }).setOrigin(0.5).setAngle(9));
    this.caption = add(this.add.text(450, 810, '', { fontFamily: 'Nunito, sans-serif', fontSize: '32px', fontStyle: 'bold',
      color: '#613e65', align: 'center', stroke: '#fff7e5', strokeThickness: 7, wordWrap: { width: 810 } }).setOrigin(0.5));
    this.layout(); this.scale.on('resize', this.layout, this);
    const offSkip = eventBus.on('halloweenBoss:introSkip', () => this.finish());
    const escape = () => this.finish();
    this.input.keyboard?.on('keydown-ESC', escape);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.scale.off('resize', this.layout, this); offSkip();
      this.input.keyboard?.off('keydown-ESC', escape);
      // An arena shutdown is an abort: never start a fight from a stale callback.
      this.complete = undefined; this.finished = true;
    });
    this.scene.bringToTop();
    this.draw(0);
  }
  private layout(): void {
    const {width, height} = this.scale;
    this.background.setSize(width, height); this.shade.setSize(width, height);
    const scale = Math.min(width / 900, Math.max(1, height - 75) / 900);
    this.stage.setPosition(width / 2 - 450 * scale, height / 2 - 425 * scale).setScale(scale);
  }
  private finish(): void {
    if (this.finished) return;
    this.finished = true;
    const callback = this.complete; this.complete = undefined;
    this.scene.stop(); callback?.();
  }
  private draw(t: number): void {
    const full = smooth((t - 4300) / 650);
    let chewing = 0;
    let eating = false;
    for (const candy of this.candies) {
      const p = clamp((t - candy.start) / 670), e = p * p;
      candy.sprite.setPosition(Phaser.Math.Linear(candy.x, 450, e), Phaser.Math.Linear(candy.y, 465, e) - Math.sin(p * Math.PI) * 175)
        .setDisplaySize(53 * (1 - p * 0.75), 53 * (1 - p * 0.75)).setAngle(p * 240)
        .setAlpha(p >= 1 ? 0 : 1);
      if (p > 0.5 && p < 1) eating = true;
      chewing += Math.sin(clamp((t - candy.start - 570) / 280) * Math.PI) * 0.018;
    }
    const hiccup = Math.sin(clamp((t - 6800) / 480) * Math.PI);
    const bob = Math.sin(t / 210) * (t < 1300 ? 6 : 2);
    this.boss.setTexture(t > 4400 ? `${FACE}_full` : eating ? `${FACE}_eat` : FACE)
      .setDisplaySize(440 * (1 + full * 0.11 + chewing - hiccup * 0.035), 440 * (1 + full * 0.055 - chewing + hiccup * 0.055))
      .setPosition(450, 410 + bob - hiccup * 34).setAngle(Math.sin(t / 200) * (eating ? 3 : 0.8));
    this.shadow.setScale(1 + full * 0.1 - hiccup * 0.1);
    const peek = smooth((t - 5050) / 500) * (1 - smooth((t - 7450) / 400));
    this.belly.setAlpha(peek).setY(549 + bob - hiccup * 34).setScale(1 + Math.sin(t / 180) * 0.025);
    this.bellyCandies.forEach((candy, i) => candy.setAngle(Math.sin(t / 220 + i) * 25));
    this.sparkles.forEach((star, i) => {
      const a = i * Math.PI / 6 + t / 4000;
      star.setPosition(450 + Math.cos(a) * (290 + Math.sin(t / 220 + i) * 12), 430 + Math.sin(a) * 225)
        .setAlpha(peek * (0.4 + Math.sin(t / 140 + i) * 0.3)).setAngle(t / 15);
    });
    this.comic.setText(t > 6800 && t < 7460 ? 'Hik!' : t > 4300 && t < 5350 ? 'Mniam!' : eating ? 'Chrup!' : '')
      .setScale(1 + Math.sin(t / 110) * 0.06);
    this.caption.setText(t < 1400 ? 'Ooo… cukierki!' : t < 4900 ? 'Ale z niej łakomczuch!' : 'Brzuszek pełen słodyczy!');
    const alpha = smooth(t / 280) * (1 - smooth((t - 7950) / 450));
    this.stage.setAlpha(alpha); this.background.setAlpha(alpha); this.shade.setAlpha(alpha);
  }
  update(_time: number, delta: number): void {
    if (this.finished) return;
    // Advance only visible frames: returning from a background tab cannot skip the story.
    this.elapsed += Math.min(delta, 80);
    this.draw(this.elapsed);
    if (this.elapsed >= DURATION) this.finish();
  }
}
